import { useEffect, useState } from 'react';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { CircularProgress, Grid, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getFormattedDate } from 'helpers/dates';
import { dispatch, useSelector } from 'store';
import { rowsPerPagePagination } from 'store/constant';
import { getBookingListDataAndFilters } from 'store/slices/memberPriority';
import MainCard from 'ui-component/cards/MainCard';
import EnhancedTableHead from 'views/forms/tables/TableEnhanced';
import { BOOKINGS_TYPE, bookingHeadCells } from '../constant';
import useStyles from '../styles';
import DateTypeFilter from './dateTypeFilter';

const MemberBookings = () => {
    const theme = useTheme();
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(true);
    const memberPriorityData = useSelector((state) => state?.memberPriority);
    const { memberBookings = {}, memberBookingsFilter = {}, selectedMember = {}, bookingListData = {} } = memberPriorityData || {};
    const { bookings = {} } = memberBookings || {};

    const {
        count = 0,
        next = null,
        previous = null,
        bookingList = [],
        page_size: rowsPerPage = rowsPerPagePagination,
        currentPage = 0
    } = bookingListData || {};

    const { code: memberCode = '' } = selectedMember || {};

    const {
        flight_bookings: flightBookings = {},
        hotel_bookings: hotelBookings = {},
        holidays_bookings: holidayBookings = {}
    } = bookings || {};

    const { count: flightBookingsCount = '', last_created_at: flightLastBookingAt = '' } = flightBookings || {};
    const { count: hotelBookingsCount = '', last_created_at: hotelLastBookingAt = '' } = hotelBookings || {};
    const { count: holidayBookingsCount = '', last_created_at: holidayLastBookingAt = '' } = holidayBookings || {};

    const handleLoadingState = (loading = false) => {
        setIsLoading(loading);
    };

    const redirectToUrl = (url = '') => {
        window.open(url, '_blank');
    };

    const handleChangePage = (_, nextPage) => {
        if (nextPage - currentPage > 0) {
            // next
            dispatch(getBookingListDataAndFilters(memberCode, next, '', nextPage));
        } else if (nextPage - currentPage < 0) {
            // previous
            dispatch(getBookingListDataAndFilters(memberCode, previous, '', nextPage));
        }
    };

    useEffect(() => {
        if (!memberCode) return;
        dispatch(getBookingListDataAndFilters(memberCode, '', memberBookingsFilter));
    }, [memberCode]);

    return (
        <Grid container item spacing={3} direction="column" xs={12}>
            <Grid container item xs={12} alignItems="center" justifyContent="space-between">
                <Grid xs={2} container item alignItems="center" spacing={0.5}>
                    <Grid item>
                        <FilterAltIcon sx={{ color: theme.palette.primary.dark }} />
                    </Grid>
                    <Grid item>
                        <Typography variant="h4" sx={{ fontWeight: 500 }}>
                            Filters
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={10}>
                    <DateTypeFilter type={BOOKINGS_TYPE} isLoading={isLoading} handleLoadingState={handleLoadingState} />
                </Grid>
            </Grid>
            {isLoading ? (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Grid>
            ) : (
                <Grid container item spacing={3}>
                    <Grid container item spacing={3} xs={12}>
                        <Grid item xs={4}>
                            <MainCard>
                                <Grid container item xs={12} spacing={0.7}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5" sx={{ color: theme.palette.grey.primary, fontWeight: 400 }}>
                                            Flights
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h4" sx={{ fontWeight: 900, color: theme.palette.grey[900] }}>
                                            {flightBookingsCount || '0'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 400, color: theme.palette.grey.primary }}>
                                            {`Last Booking Date - ${getFormattedDate(flightLastBookingAt)}`}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </MainCard>
                        </Grid>
                        <Grid item xs={4}>
                            <MainCard>
                                <Grid container item xs={12} spacing={0.7}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5" sx={{ color: theme.palette.grey.primary, fontWeight: 400 }}>
                                            Hotels
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h4" sx={{ fontWeight: 900, color: theme.palette.grey[900] }}>
                                            {hotelBookingsCount || '0'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 400, color: theme.palette.grey.primary }}>
                                            {`Last Booking Date - ${getFormattedDate(hotelLastBookingAt)}`}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </MainCard>
                        </Grid>
                        <Grid item xs={4}>
                            <MainCard>
                                <Grid container item xs={12} spacing={0.7}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5" sx={{ color: theme.palette.grey.primary, fontWeight: 400 }}>
                                            Holidays
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h4" sx={{ fontWeight: 900, color: theme.palette.grey[900] }}>
                                            {holidayBookingsCount || '0'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 400, color: theme.palette.grey.primary }}>
                                            {`Last Booking Date - ${getFormattedDate(holidayLastBookingAt)}`}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </MainCard>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} spacing={1.5}>
                        <Grid item container xs={12} justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Typography variant="h4">All Bookings</Typography>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <MainCard content={false}>
                                {/* table */}
                                <TableContainer style={{ maxHeight: '450px', overflowY: 'auto' }}>
                                    <Table stickyHeader sx={{ minWidth: 350 }} aria-label="simple table">
                                        <EnhancedTableHead headCells={bookingHeadCells} tableCellClass={classes.tableCell} />
                                        <TableBody>
                                            {bookingList.map((booking) => {
                                                const {
                                                    id = '',
                                                    code: bookingCode = '',
                                                    status: bookingStatus = '',
                                                    category = {},
                                                    destination = {},
                                                    amount = '',
                                                    amount_in_inr: amtInInr = '',
                                                    created_at: createdAt = '',
                                                    departure_at: departureAt = ''
                                                } = booking || {};
                                                const { name: categoryName = '' } = category || {};

                                                return (
                                                    <TableRow hover key={id}>
                                                        <TableCell
                                                            sx={{
                                                                minWidth: '110px',
                                                                cursor: 'pointer',
                                                                color: theme.palette.primary.dark
                                                            }}
                                                            component="th"
                                                            scope="booking"
                                                            onClick={() => redirectToUrl(`/booking-list/${bookingCode}`)}
                                                        >
                                                            {bookingCode}
                                                        </TableCell>
                                                        <TableCell sx={{ minWidth: '140px' }}>{categoryName || '-'}</TableCell>
                                                        <TableCell sx={{ minWidth: '120px' }}>{bookingStatus || '-'}</TableCell>
                                                        <TableCell sx={{ minWidth: '150px' }}>
                                                            {createdAt ? `${getFormattedDate(createdAt, 'dd-mm-yyyy', true)}` : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ minWidth: '150px' }}>{destination?.name || '-'}</TableCell>
                                                        <TableCell sx={{ minWidth: '150px' }}>
                                                            {departureAt ? `${getFormattedDate(departureAt, 'dd-mm-yyyy', true)}` : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ minWidth: '220px' }}>{amount || '-'}</TableCell>
                                                        <TableCell sx={{ minWidth: '140px' }}>{amtInInr || '-'}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    rowsPerPageOptions={[rowsPerPage]}
                                    component="div"
                                    rowsPerPage={rowsPerPage}
                                    page={currentPage}
                                    count={parseInt(count, 10)}
                                    backIconButtonProps={{
                                        'aria-label': 'previous page'
                                    }}
                                    nextIconButtonProps={{
                                        'aria-label': 'next page'
                                    }}
                                    onPageChange={handleChangePage}
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `page ${currentPage + 1} of ${Math.ceil(parseInt(count, 10) / rowsPerPage)}`
                                    }
                                    sx={{ '& .MuiTablePagination-toolbar': { padding: 0 } }}
                                />
                            </MainCard>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

export default MemberBookings;
